import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { formatCompactNumber } from '../lib/format.ts';
import { ChartFrame, ChartTooltip } from './ChartFrame.tsx';
import type { ChartBaseProps } from './ChartFrame.tsx';
import { PLOT, ThresholdLine, XAxis, YAxis, bandTickShown } from './axes.tsx';
import { MAX_SERIES, colourFor } from './palette.ts';

export interface StackKey {
  key: string;
  label: string;
}

export interface StackDatum {
  /** The bar's label on the x axis: "Oct 16", "Mon". */
  label: string;
  values: Record<string, number>;
}

export interface StackedBarChartProps extends ChartBaseProps {
  /** The parts, bottom first. At most 6; fold the rest into "Other" before passing them. */
  keys: StackKey[];
  data: StackDatum[];
  format?: (n: number) => string;
  /** Formats the axis ticks (default `format`); money axes read best whole: formatMoneyCompact. */
  axisFormat?: (n: number) => string;
  threshold?: { value: number; label: string };
  /** Every series key the page knows, in slot order. */
  order?: string[];
}

/**
 * How parts make up a total over time (data-viz.md): stacked bars from zero, 2 px between
 * bars and between segments, the top segment rounded 2 px. A single key is a plain bar chart
 * over time. Hover or focus a bar to read every part and the total.
 */
export function StackedBarChart({ keys, data, format = formatCompactNumber, axisFormat, threshold, order, height = 200, ...base }: StackedBarChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  // One tab stop for the whole chart (the last bar, or the one last read); arrows, Home and End
  // move between bars, as in LineChart. Every bar used to be a tab stop: 30 presses to cross.
  const [cursor, setCursor] = useState<number | null>(null);
  const shown = keys.slice(0, MAX_SERIES);
  const slotKeys = order ?? shown.map((k) => k.key);
  const totals = data.map((d) => shown.reduce((n, k) => n + Math.max(0, d.values[k.key] ?? 0), 0));
  const legend = shown.map((k) => ({ key: k.key, label: k.label, colour: colourFor(k.key, slotKeys) }));
  const table = {
    columns: ['', ...shown.map((k) => k.label), 'Total'],
    rows: data.map((d, i) => [d.label, ...shown.map((k) => format(d.values[k.key] ?? 0)), format(totals[i] ?? 0)]),
  };

  return (
    <ChartFrame {...base} height={height} isEmpty={data.length === 0 || totals.every((t) => t === 0)} legend={legend} table={table}>
      {(width) => {
        const iw = Math.max(0, width - PLOT.yAxisBand - PLOT.tail);
        const ih = Math.max(0, height - PLOT.headroom - PLOT.xAxisBand);
        const x = scaleBand<string>({ domain: data.map((_, i) => String(i)), range: [0, iw], padding: 0.2 });
        const y = scaleLinear<number>({ domain: [0, Math.max(1, ...totals, threshold?.value ?? 0)], range: [ih, 0], nice: true });
        // At most 6 labels, and never closer than about 64 px apart on a narrow chart.
        const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.min(6, Math.floor(iw / 64)))));
        const active = hover == null ? null : data[hover];
        const stop = cursor != null && cursor < data.length ? cursor : data.length - 1;
        const onBarKey = (e: KeyboardEvent<SVGGElement>, i: number) => {
          const last = data.length - 1;
          const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? Math.min(last, i + 1) : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? Math.max(0, i - 1) : e.key === 'Home' ? 0 : e.key === 'End' ? last : null;
          if (next == null) return;
          e.preventDefault();
          setCursor(next);
          e.currentTarget.parentElement?.querySelector<SVGGElement>(`[data-bar-index="${next}"]`)?.focus();
        };
        return (
          <>
            <svg width={width} height={height} className="overflow-visible">
              <Group left={PLOT.yAxisBand} top={PLOT.headroom}>
                <YAxis scale={y} width={iw} format={axisFormat ?? format} />
                <XAxis
                  scale={x}
                  top={ih}
                  numTicks={data.length}
                  format={(i: string) => (bandTickShown(Number(i), data.length, every) ? (data[Number(i)]?.label ?? '') : '')}
                />
                {threshold && <ThresholdLine y={y(threshold.value)} width={iw} label={threshold.label} />}
                {data.map((d, i) => {
                  const bx = x(String(i)) ?? 0;
                  const bw = x.bandwidth();
                  let base0 = 0;
                  const parts = shown.map((k) => ({ k, v: Math.max(0, d.values[k.key] ?? 0) })).filter((p) => p.v > 0);
                  return (
                    <g
                      key={i}
                      data-bar-index={i}
                      tabIndex={i === stop ? 0 : -1}
                      role="img"
                      aria-label={`${d.label}: ${parts.map((p) => `${p.k.label} ${format(p.v)}`).join(', ')}, total ${format(totals[i] ?? 0)}`}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover(null)}
                      onFocus={() => {
                        setHover(i);
                        setCursor(i);
                      }}
                      onKeyDown={(e) => onBarKey(e, i)}
                      onBlur={() => setHover(null)}
                      opacity={hover != null && hover !== i ? 0.3 : 1}
                      className="outline-none"
                    >
                      {parts.map((p, pi) => {
                        const y0 = y(base0);
                        base0 += p.v;
                        const y1 = y(base0);
                        // 2 px of surface between stacked segments.
                        const gap = pi > 0 ? 2 : 0;
                        const h = Math.max(0, y0 - y1 - gap);
                        const top = pi === parts.length - 1;
                        return top ? (
                          <path key={p.k.key} d={roundedTop(bx, y1, bw, h, 2)} fill={colourFor(p.k.key, slotKeys)} data-segment={p.k.key} />
                        ) : (
                          <rect key={p.k.key} x={bx} y={y1} width={bw} height={h} fill={colourFor(p.k.key, slotKeys)} data-segment={p.k.key} />
                        );
                      })}
                      <rect x={bx} y={0} width={bw} height={ih} fill="transparent" />
                    </g>
                  );
                })}
              </Group>
            </svg>
            {active && hover != null && (
              <ChartTooltip
                width={width}
                left={PLOT.yAxisBand + (x(String(hover)) ?? 0) + x.bandwidth() / 2}
                top={PLOT.headroom + y(totals[hover] ?? 0)}
                heading={`${active.label}, total ${format(totals[hover] ?? 0)}`}
                rows={[...shown]
                  .reverse()
                  .map((k) => ({ key: k.key, label: k.label, colour: colourFor(k.key, slotKeys), value: format(active.values[k.key] ?? 0) }))}
              />
            )}
          </>
        );
      }}
    </ChartFrame>
  );
}

/** A bar path with only its top corners rounded. */
export function roundedTop(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}
